// SMSF Calculator Comprehensive Tutorial System
// Covers live SMSF suite tabs across 8 categories (incl. Division 296)
// Last Updated: October 2026

import React, { useState, useEffect } from 'react';
import { FinancialConfig } from './FinancialConfig.js';

const fmtAud = (n) => `$${Number(n).toLocaleString('en-AU')}`;

const SMSFTutorialSystem = ({ smsfState, setSmsfState, onClose, renderInPanel = false }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const contentRef = React.useRef(null);
    const concessionalCap = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
    const nccCap = FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL;
    const tbc = FinancialConfig.TRANSFER_BALANCE_CAP;
    const co = FinancialConfig.CONTRIBUTION_CAPS.CO_CONTRIBUTION;
    const d296 = FinancialConfig.DIVISION_296;

    // Note: Scroll is handled in button onClick handlers for immediate response

    const tutorialSteps = [
        {
            id: 'welcome',
            title: 'Welcome to SMSF Calculator Suite',
            category: 'intro',
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700 text-lg">
                        This SMSF calculator suite includes <strong>37 specialized tools</strong> across eight categories,
                        covering Self-Managed Super Fund administration, compliance, tax (including Division 296), and investment strategy.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-3">8 Major Categories:</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-blue-100 p-3 rounded">
                                <div className="font-semibold text-blue-900">📊 Overview (4 tabs)</div>
                                <div className="text-xs text-blue-700 mt-1">Personal/fund details, dashboard, wizard, SMSF vs industry</div>
                            </div>
                            <div className="bg-green-100 p-3 rounded">
                                <div className="font-semibold text-green-900">💰 Contributions (9 tabs)</div>
                                <div className="text-xs text-green-700 mt-1">Carry-forward, bring-forward, co-contribution, downsizer, Div 293, Div 296, work test, FHSS, spouse splitting</div>
                            </div>
                            <div className="bg-purple-100 p-3 rounded">
                                <div className="font-semibold text-purple-900">🏦 Investment & Assets (7 tabs)</div>
                                <div className="text-xs text-purple-700 mt-1">Strategy, ATO-compliant strategy, LRBA, limits, collectibles, related parties, valuations</div>
                            </div>
                            <div className="bg-orange-100 p-3 rounded">
                                <div className="font-semibold text-orange-900">💹 Pensions & Income (2 tabs)</div>
                                <div className="text-xs text-orange-700 mt-1">Quick Check, tax harvesting</div>
                            </div>
                            <div className="bg-red-100 p-3 rounded">
                                <div className="font-semibold text-red-900">✅ Compliance & Admin (3 tabs)</div>
                                <div className="text-xs text-red-700 mt-1">Compliance checks, enhanced monitoring, administration</div>
                            </div>
                            <div className="bg-yellow-100 p-3 rounded">
                                <div className="font-semibold text-yellow-900">📈 Planning & Analysis (6 tabs)</div>
                                <div className="text-xs text-yellow-700 mt-1">Analytics, risk, performance, insurance, estate, succession</div>
                            </div>
                            <div className="bg-indigo-100 p-3 rounded">
                                <div className="font-semibold text-indigo-900">📄 Reporting (4 tabs)</div>
                                <div className="text-xs text-indigo-700 mt-1">Reports, email, history, data export</div>
                            </div>
                            <div className="bg-pink-100 p-3 rounded">
                                <div className="font-semibold text-pink-900">🚀 Advanced (2 tabs)</div>
                                <div className="text-xs text-pink-700 mt-1">Strategy builder, enhanced features</div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">💡 How to Use This Tutorial:</h4>
                        <ul className="list-disc list-inside text-yellow-700 text-sm space-y-1">
                            <li>Navigate with Previous/Next buttons or click dots below</li>
                            <li>Each step covers 1-2 related tabs in detail</li>
                            <li>Click any tab in the calculator to explore while learning</li>
                            <li>Come back anytime by clicking the 🎓 Tutorial button</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'what-smsf-does',
            title: 'What SMSF Calculator Does (and Doesn\'t Do)',
            category: 'intro',
            content: (
                <div className="space-y-4">
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                        <h4 className="font-semibold text-green-800 mb-2">✅ What SMSF Calculator DOES</h4>
                        <p className="text-sm text-green-700 mb-2">
                            The SMSF Calculator is a <strong>compliance and tax planning tool</strong> for point-in-time calculations:
                        </p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Contribution caps</strong> - How much you can contribute this year ({fmtAud(concessionalCap)} concessional, {fmtAud(nccCap)} non-concessional for 2026-27)</li>
                            <li><strong>Division 293 tax</strong> - Additional 15% tax for high earners (income + contributions &gt; $250k)</li>
                            <li><strong>Division 296 tax</strong> - Additional tax on earnings attributable to TSB above {fmtAud(d296.THRESHOLD)} (from {d296.FIRST_YEAR_FINANCIAL_YEAR})</li>
                            <li><strong>Pension / TBC planning</strong> - Split between pension (general Transfer Balance Cap {fmtAud(tbc)}) and accumulation</li>
                            <li><strong>Carry-forward</strong> - Unused caps from last 5 years (if balance &lt;$500k)</li>
                            <li><strong>LRBA</strong> - Property borrowing capacity and loan calculations</li>
                            <li><strong>Fee analysis</strong> - SMSF vs. retail fund cost comparison</li>
                            <li><strong>Compliance checks</strong> - In-house assets, related party rules, sole purpose test</li>
                        </ul>
                    </div>
                    
                    <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                        <h4 className="font-semibold text-red-800 mb-2">❌ What SMSF Calculator DOES NOT Do</h4>
                        <p className="text-sm text-red-700 mb-2">
                            SMSF Calculator is <strong>NOT a retirement planning calculator</strong>. It does NOT:
                        </p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li>❌ Simulate retirement over 30-40 years → <strong>Use Advanced Calculator</strong></li>
                            <li>❌ Calculate Age Pension eligibility over time → <strong>Use Basic or Advanced Calculator</strong></li>
                            <li>❌ Model investment returns year-by-year → <strong>Use Advanced Calculator</strong></li>
                            <li>❌ Tell you if super will last until age 95 → <strong>Use Basic Calculator</strong></li>
                            <li>❌ Optimize withdrawal strategies (Fixed, Dynamic SWR) → <strong>Use Advanced Calculator</strong></li>
                            <li>❌ Model phased retirement scenarios → <strong>Use Advanced Calculator</strong></li>
                        </ul>
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">🔄 How SMSF Works With Other Calculators</h4>
                        <div className="text-sm text-blue-700 space-y-3">
                            <div className="flex items-start space-x-2">
                                <span className="text-2xl">1️⃣</span>
                                <div>
                                    <strong>SMSF Calculator</strong> → Provides current snapshot and compliance data
                                    <div className="text-xs text-blue-600 mt-1">Get balances, fees, TBC usage, contribution caps</div>
                                </div>
                            </div>
                            <div className="flex items-center justify-center text-blue-400">↓</div>
                            <div className="flex items-start space-x-2">
                                <span className="text-2xl">2️⃣</span>
                                <div>
                                    <strong>Accumulation Calculator</strong> → Projects growth to retirement
                                    <div className="text-xs text-blue-600 mt-1">If still working: Model salary + contributions until retirement</div>
                                </div>
                            </div>
                            <div className="flex items-center justify-center text-blue-400">↓</div>
                            <div className="flex items-start space-x-2">
                                <span className="text-2xl">3️⃣</span>
                                <div>
                                    <strong>Advanced Calculator</strong> → Tests withdrawal strategies over 30+ years
                                    <div className="text-xs text-blue-600 mt-1">Historical simulation, Monte Carlo, dynamic strategies, Age Pension</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-purple-800 mb-2">📤 What to Export from SMSF</h4>
                        <table className="w-full text-sm text-purple-700">
                            <thead className="bg-purple-100">
                                <tr>
                                    <th className="text-left p-2">SMSF Output</th>
                                    <th className="text-left p-2">Transfer To</th>
                                    <th className="text-left p-2">Calculator Input Field</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-purple-200">
                                <tr>
                                    <td className="p-2">Your current balance</td>
                                    <td className="p-2">Advanced</td>
                                    <td className="p-2 font-mono text-xs">yourSuper</td>
                                </tr>
                                <tr>
                                    <td className="p-2">Partner balance</td>
                                    <td className="p-2">Advanced</td>
                                    <td className="p-2 font-mono text-xs">partnerSuper</td>
                                </tr>
                                <tr>
                                    <td className="p-2">Admin fees ($52)</td>
                                    <td className="p-2">Advanced</td>
                                    <td className="p-2 font-mono text-xs">adminFees</td>
                                </tr>
                                <tr>
                                    <td className="p-2">Investment fees (0.5%)</td>
                                    <td className="p-2">Advanced</td>
                                    <td className="p-2 font-mono text-xs">investmentFees</td>
                                </tr>
                                <tr>
                                    <td className="p-2">TBC used</td>
                                    <td className="p-2">Advanced</td>
                                    <td className="p-2 font-mono text-xs">yourTbcUsed</td>
                                </tr>
                                <tr>
                                    <td className="p-2">Asset allocation (%)</td>
                                    <td className="p-2">Advanced</td>
                                    <td className="p-2 font-mono text-xs">sp500, ausShares, bonds, etc.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">🎯 Quick Reference: When to Use Each Calculator</h4>
                        <div className="text-sm text-yellow-700 space-y-2">
                            <div><strong>"How much can I contribute this year?"</strong> → SMSF: Contribution Caps</div>
                            <div><strong>"Will I pay Division 293 tax?"</strong> → SMSF: Division 293</div>
                            <div><strong>"How much super at retirement?"</strong> → Accumulation Calculator</div>
                            <div><strong>"Will my super last 30 years?"</strong> → Basic or Advanced Calculator</div>
                            <div><strong>"What's my sustainable income?"</strong> → Advanced Calculator</div>
                            <div><strong>"Dynamic SWR vs Fixed withdrawal?"</strong> → Advanced Calculator</div>
                            <div><strong>"Is SMSF cheaper than retail super?"</strong> → SMSF: Fee Analysis</div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'overview',
            title: 'Overview & Setup Tabs',
            category: 'setup',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Category 1: Overview & Setup (4 tabs)</h4>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">👤 1. Personal/Fund Details Tab</h5>
                        <p className="text-sm text-blue-700 mb-2">Core SMSF information:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Fund name, ABN, TFN:</strong> Official registration details</li>
                            <li><strong>Establishment date:</strong> When SMSF was created</li>
                            <li><strong>Trustee type:</strong> Individual vs Corporate (affects compliance)</li>
                            <li><strong>Number of members:</strong> 1-6 members maximum</li>
                            <li><strong>Member details:</strong> Age, balance, risk tolerance, investment goals</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Why it matters:</strong> Foundation for all other tabs, affects compliance requirements
                        </div>
                        <div className="mt-3 p-3 bg-gradient-to-r from-blue-100 to-purple-100 rounded border-l-4 border-blue-500">
                            <h6 className="font-semibold text-blue-900 mb-1 text-sm">🔄 Transfer to Retirement Calculators</h6>
                            <p className="text-xs text-blue-800 mb-2">At the top of this tab, you'll find 3 transfer buttons that automatically populate your SMSF data into retirement planning calculators:</p>
                            <ul className="list-disc list-inside text-blue-700 text-xs space-y-1 ml-3">
                                <li><strong>Transfer to Accumulation:</strong> Model pre-retirement savings and contributions</li>
                                <li><strong>Transfer to Basic Calculator:</strong> Simple retirement income planning</li>
                                <li><strong>Transfer to Advanced Calculator:</strong> Comprehensive planning with Monte Carlo simulation and 4 withdrawal strategies</li>
                            </ul>
                            <div className="mt-2 p-2 bg-blue-50 text-xs text-blue-900 rounded">
                                <strong>How it works:</strong> Click a button → Your ages, balances, fees, and asset allocation are automatically filled in the target calculator → Calculator opens ready to run → Data expires after 5 minutes if unused (security feature)
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📊 2. Dashboard Tab</h5>
                        <p className="text-sm text-green-700 mb-2">Real-time compliance and performance summary:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>At-a-glance status:</strong> Green = compliant, Yellow = warning, Red = issue</li>
                            <li><strong>Key metrics:</strong> Total assets, contribution caps used, compliance alerts</li>
                            <li><strong>Action items:</strong> What needs attention now</li>
                            <li><strong>Performance summary:</strong> Returns vs benchmarks</li>
                        </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🧙‍♂️ 3. Setup Wizard Tab</h5>
                        <p className="text-sm text-purple-700 mb-2">Guided setup for new SMSF users:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li>Step-by-step fund configuration</li>
                            <li>Validates inputs as you go</li>
                            <li>Explains each decision point</li>
                            <li>Designed for first-time SMSF trustees</li>
                        </ul>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">⚖️ 4. SMSF vs Industry Fund Comparison</h5>
                        <p className="text-sm text-orange-700 mb-2">Should you have an SMSF?</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Cost comparison:</strong> SMSF setup/admin vs industry fund fees</li>
                            <li><strong>Break-even analysis:</strong> How much super needed for SMSF to be cost-effective</li>
                            <li><strong>Rule of thumb:</strong> Need $200k-$500k+ to justify SMSF costs</li>
                            <li><strong>Feature comparison:</strong> Control vs convenience trade-offs</li>
                        </ul>
                        <div className="mt-2 p-2 bg-orange-200 text-xs text-orange-900 rounded">
                            <strong>Reality check:</strong> SMSF costs $1,500-$3,000+/year. On $200k balance, that's 0.75-1.5% in fees alone!
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'contributions',
            title: 'Contribution Tools (8 Specialized Calculators)',
            category: 'contributions',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Category 2: Contributions (8 tabs)</h4>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📅 5. Carry-Forward Tab</h5>
                        <p className="text-sm text-blue-700 mb-2">Use unused concessional caps from past 5 years:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Calculator shows:</strong> Available carry-forward amounts by year</li>
                            <li><strong>Eligibility check:</strong> TSB &lt; $500k requirement</li>
                            <li><strong>Optimization:</strong> Shows optimal contribution timing</li>
                            <li><strong>5-year tracking:</strong> Caps expire after 5 years (FIFO)</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">🔄 6. Bring-Forward Tab</h5>
                        <p className="text-sm text-green-700 mb-2">3-year non-concessional cap acceleration:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Calculator shows:</strong> Available bring-forward amount (${(FinancialConfig.BRING_FORWARD_RULE.MAXIMUM_CONTRIBUTION / 1000).toFixed(0)}k if eligible)</li>
                            <li><strong>TSB tracking:</strong> Checks ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M general transfer balance / non-concessional cutoff</li>
                            <li><strong>Age check:</strong> Must be &lt; 75</li>
                            <li><strong>Lock-in period:</strong> Shows 3-year restriction period</li>
                        </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">💚 7. Government Co-Contribution Tab</h5>
                        <p className="text-sm text-purple-700 mb-2">Calculate government matching contribution:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Income threshold testing:</strong> {fmtAud(co.MIN_INCOME_THRESHOLD)} – {fmtAud(co.MAX_INCOME_THRESHOLD)} taper range (2026-27)</li>
                            <li><strong>Contribution calculator:</strong> Shows how much to contribute for max benefit</li>
                            <li><strong>10% income rule:</strong> Checks employment income requirement</li>
                            <li><strong>Annual tracking:</strong> Can claim every year if eligible</li>
                        </ul>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">🏠 8. Downsizer Tab</h5>
                        <p className="text-sm text-orange-700 mb-2">$300k per person from home sale:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Eligibility checker:</strong> Age 55+, owned 10+ years, CGT exempt</li>
                            <li><strong>Contribution calculator:</strong> Up to $300k per person</li>
                            <li><strong>Timing optimizer:</strong> Shows impact on TSB and future caps</li>
                            <li><strong>Couple strategy:</strong> Both partners can contribute ($600k total)</li>
                        </ul>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">💵 9. Division 293 Tax Tab</h5>
                        <p className="text-sm text-red-700 mb-2">Additional 15% tax for high earners:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Threshold:</strong> Income + concessional contributions &gt; $250,000</li>
                            <li><strong>Calculator shows:</strong> Exact Div 293 tax liability</li>
                            <li><strong>Payment options:</strong> Pay from super or personally</li>
                            <li><strong>Strategy tips:</strong> Timing contributions to minimize Div 293</li>
                        </ul>
                    </div>

                    <div className="bg-indigo-50 p-4 rounded-lg">
                        <h5 className="font-medium text-indigo-800 mb-2">🏦 10. Division 296 Tax Tab</h5>
                        <p className="text-sm text-indigo-700 mb-2">
                            Additional tax on earnings attributable to large total super balances (from {d296.FIRST_YEAR_FINANCIAL_YEAR}):
                        </p>
                        <ul className="list-disc list-inside text-indigo-600 text-sm space-y-1 ml-4">
                            <li><strong>LSBT:</strong> {fmtAud(d296.THRESHOLD)} — earnings proportion above this can attract Division 296 tax</li>
                            <li><strong>VLSBT:</strong> {fmtAud(d296.VERY_LARGE_THRESHOLD)} — further tier on earnings above this level</li>
                            <li><strong>Scope:</strong> Uses Total Super Balance across <em>all</em> funds, not just the SMSF</li>
                            <li><strong>Educational:</strong> Estimate only — ATO assessment uses reported Division 296 fund earnings</li>
                        </ul>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">⏱️ 11. Work Test Tab</h5>
                        <p className="text-sm text-yellow-700 mb-2">Ages 67-74 contribution requirements:</p>
                        <ul className="list-disc list-inside text-yellow-600 text-sm space-y-1 ml-4">
                            <li><strong>40-hour rule:</strong> Must work 40 hours in 30 consecutive days</li>
                            <li><strong>Calculator tracks:</strong> Work eligibility by member age</li>
                            <li><strong>Exemption checker:</strong> One-year exemption if qualified previous year and TSB &lt; $300k</li>
                            <li><strong>Planning tool:</strong> Shows when members lose contribution eligibility</li>
                        </ul>
                    </div>

                    <div className="bg-teal-50 p-4 rounded-lg">
                        <h5 className="font-medium text-teal-800 mb-2">🏡 12. FHSS (First Home Super Saver) Tab</h5>
                        <p className="text-sm text-teal-700 mb-2">Save for first home inside super:</p>
                        <ul className="list-disc list-inside text-teal-600 text-sm space-y-1 ml-4">
                            <li><strong>Contribution limit:</strong> $15,000/year, $50,000 lifetime</li>
                            <li><strong>Calculator shows:</strong> Withdrawal amount after deemed earnings</li>
                            <li><strong>Tax benefit:</strong> Contributions taxed at 15% vs marginal rate</li>
                            <li><strong>Eligibility:</strong> First home buyers only, income and property price limits apply</li>
                        </ul>
                    </div>

                    <div className="bg-pink-50 p-4 rounded-lg">
                        <h5 className="font-medium text-pink-800 mb-2">💑 13. Spouse Contribution Splitting Tab</h5>
                        <p className="text-sm text-pink-700 mb-2">Split concessional contributions with spouse:</p>
                        <ul className="list-disc list-inside text-pink-600 text-sm space-y-1 ml-4">
                            <li><strong>Amount:</strong> Up to 85% of concessional contributions</li>
                            <li><strong>Purpose:</strong> Equalize super balances between partners</li>
                            <li><strong>Benefit:</strong> Both partners maximize ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M Transfer Balance Cap</li>
                            <li><strong>Calculator:</strong> Shows optimal split amount for tax efficiency</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'investment',
            title: 'Investment Management Tools',
            category: 'investment',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Category 3: Investment & Assets (7 tabs)</h4>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">📊 13. Investment Strategy Builder Tab</h5>
                        <p className="text-sm text-purple-700 mb-2">Create compliant investment strategy document:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Template strategies:</strong> Conservative, Balanced, Growth, High Growth</li>
                            <li><strong>Custom allocation:</strong> Set exact % for cash, fixed income, shares, property, alternatives</li>
                            <li><strong>Risk metrics:</strong> Expected return, volatility, max drawdown</li>
                            <li><strong>PDF export:</strong> Generates compliant investment strategy document</li>
                            <li><strong>Validation:</strong> Must total 100%, investment objectives min 50 characters</li>
                        </ul>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>ATO requirement:</strong> All SMSFs must have documented investment strategy reviewed annually
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🏛️ 14. LRBA (Limited Recourse Borrowing) Tab</h5>
                        <p className="text-sm text-blue-700 mb-2">Property borrowing within SMSF:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Loan structure:</strong> SMSF borrows to buy property (typically max 70-80% LVR)</li>
                            <li><strong>Calculator shows:</strong> Serviceability, cash flow, total cost</li>
                            <li><strong>Rent vs mortgage:</strong> Validates rental income covers repayments</li>
                            <li><strong>Risk analysis:</strong> Interest rate sensitivity, vacancy risk</li>
                            <li><strong>Compliance checks:</strong> Related party rules, sole purpose test</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Warning:</strong> LRBA is complex and risky. Property illiquidity can trap your SMSF.
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📊 15. Investment Limits Monitor Tab</h5>
                        <p className="text-sm text-green-700 mb-2">Track regulatory investment restrictions:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>5% in-house asset rule:</strong> Max 5% in related party assets</li>
                            <li><strong>Single asset concentration:</strong> Warns if over-invested in one asset</li>
                            <li><strong>Liquidity requirements:</strong> Must have cash for pensions and expenses</li>
                            <li><strong>Real-time monitoring:</strong> Updates as you enter investments</li>
                        </ul>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">🎨 16. Collectibles & Personal Use Assets Tab</h5>
                        <p className="text-sm text-orange-700 mb-2">Art, wine, cars, jewelry in your SMSF:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Allowed items:</strong> Art, antiques, jewelry, wine, cars, memorabilia</li>
                            <li><strong>Storage requirements:</strong> Cannot be used personally, must be stored appropriately</li>
                            <li><strong>Valuation:</strong> Must be independently valued regularly</li>
                            <li><strong>Insurance tracking:</strong> Required for high-value items</li>
                            <li><strong>Prohibition check:</strong> Cannot lease to members or relatives</li>
                        </ul>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">👥 17. Related Party Transactions Tab</h5>
                        <p className="text-sm text-red-700 mb-2">Monitor transactions with related parties:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>5% rule:</strong> In-house assets max 5% of fund value</li>
                            <li><strong>Prohibited:</strong> Loans to members, residential property leases</li>
                            <li><strong>Allowed (with conditions):</strong> Business real property leases at market rate</li>
                            <li><strong>Audit trail:</strong> Tracks and documents all related party dealings</li>
                        </ul>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">🏠 18. Asset Valuation Tab</h5>
                        <p className="text-sm text-yellow-700 mb-2">Market value tracking and compliance:</p>
                        <ul className="list-disc list-inside text-yellow-600 text-sm space-y-1 ml-4">
                            <li><strong>Annual valuations:</strong> All assets must be valued at market</li>
                            <li><strong>Valuation methods:</strong> Independent vs self-assessed (depends on asset type)</li>
                            <li><strong>Property valuations:</strong> Quarterly for LRBA, annually otherwise</li>
                            <li><strong>Audit requirements:</strong> Documents valuation methodology</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'pension',
            title: 'Pension Optimization & Tax Tools',
            category: 'pension',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Category 4: Pensions & Income (2 tabs)</h4>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">⚡ 19. Quick Retirement Check Tab</h5>
                        <p className="text-sm text-green-700 mb-2">Fast 30-second retirement projection snapshot:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>3-Scenario Analysis:</strong> Optimistic (9%), Realistic (7%), Pessimistic (5%) returns</li>
                            <li><strong>Pension Type Comparison:</strong> Account-Based, TTR, Lifetime Annuity characteristics</li>
                            <li><strong>Lump Sum Planning:</strong> Model one-off withdrawals in specific years</li>
                            <li><strong>Quick Check Only:</strong> For comprehensive analysis, use Advanced Calculator</li>
                            <li><strong>Minimum drawdown:</strong> Age-based minimums (e.g., 65-74 = 5%)</li>
                            <li><strong>Drawdown strategies:</strong> Minimum, optimal, or custom percentage</li>
                            <li><strong>Tax comparison:</strong> Accumulation (15% tax) vs Pension (0% tax) earnings</li>
                            <li><strong>Centrelink impact:</strong> Account-based pension treatment in assets/income tests</li>
                        </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Key insight:</strong> Over 60 with ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M+ in super? Put up to the Transfer Balance Cap in pension (0% tax), rest in accumulation
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📉 20. Tax Loss Harvesting Tab</h5>
                        <p className="text-sm text-blue-700 mb-2">Optimize capital gains tax through strategic asset sales:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Loss identification:</strong> Find underperforming assets with capital losses</li>
                            <li><strong>Expense modeling:</strong> Admin fees, pension payments, tax liabilities</li>
                            <li><strong>Liquidity analysis:</strong> Ensure enough cash for upcoming obligations</li>
                            <li><strong>Timing optimization:</strong> When to sell assets to fund pensions</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Use case:</strong> "Can I pay $80k pension this year without selling property?"
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">📉 21. Tax Loss Harvesting Tab</h5>
                        <p className="text-sm text-purple-700 mb-2">Minimize capital gains tax (accumulation phase only):</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Loss identification:</strong> Which holdings are in loss position</li>
                            <li><strong>Gain offset calculator:</strong> Realize losses to offset gains</li>
                            <li><strong>Wash sale rules:</strong> 45-day holding period warnings</li>
                            <li><strong>Optimal timing:</strong> When to harvest losses (typically end of financial year)</li>
                            <li><strong>Not relevant in pension phase:</strong> CGT is 0% anyway</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'compliance',
            title: 'Compliance & Administration',
            category: 'compliance',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Category 5: Compliance (3 tabs)</h4>
                    
                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">✅ 22. Compliance Dashboard Tab</h5>
                        <p className="text-sm text-red-700 mb-2">Real-time compliance monitoring:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Contribution cap checks:</strong> Concessional and non-concessional against limits</li>
                            <li><strong>TBC monitoring:</strong> ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M transfer balance cap compliance</li>
                            <li><strong>In-house asset test:</strong> Must be &lt; 5% of total assets</li>
                            <li><strong>Minimum pension drawdowns:</strong> Age-based minimum withdrawal checking</li>
                            <li><strong>Sole purpose test:</strong> Flags potential breaches</li>
                        </ul>
                        <div className="mt-2 p-2 bg-red-200 text-xs text-red-900 rounded">
                            <strong>Traffic light system:</strong> 🟢 Compliant | 🟡 Warning | 🔴 Breach
                        </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">🛡️ 23. Enhanced Compliance Tab</h5>
                        <p className="text-sm text-orange-700 mb-2">Deep-dive compliance analysis:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Trustee requirements:</strong> Director vs individual trustee compliance</li>
                            <li><strong>Deed compliance:</strong> Operating within trust deed rules</li>
                            <li><strong>Arm's length transactions:</strong> Market rate validation for all dealings</li>
                            <li><strong>Investment restrictions:</strong> No loans to members, no residential to relatives</li>
                            <li><strong>Audit preparation:</strong> Checklist for annual SMSF audit</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📋 24. Administration Tasks Tab</h5>
                        <p className="text-sm text-blue-700 mb-2">Day-to-day SMSF administration:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Trustee meetings:</strong> Minutes template and scheduling</li>
                            <li><strong>Annual tasks:</strong> Tax return, audit, annual return, member statements</li>
                            <li><strong>Quarterly tasks:</strong> Contribution monitoring, pension payments</li>
                            <li><strong>Document storage:</strong> What records to keep (5-10 years)</li>
                            <li><strong>Lodgement deadlines:</strong> ATO reporting due dates</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Cost estimate:</strong> DIY = 100+ hours/year | Professional = $1,500-$3,000/year
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'investment-strategy-requirement',
            title: 'Investment Strategy (Legal Requirement)',
            category: 'compliance',
            content: (
                <div className="space-y-4">
                    <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-600">
                        <h4 className="font-semibold text-red-800 mb-2">⚖️ Legal Requirement (Regulation 4.09 SIS Act)</h4>
                        <p className="text-sm text-red-700 mb-2">
                            <strong>EVERY SMSF MUST have a written investment strategy.</strong> This is not optional.
                        </p>
                        <p className="text-sm text-red-700">
                            Failure to maintain a compliant investment strategy can result in:
                        </p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4 mt-2">
                            <li>ATO penalties</li>
                            <li>Non-complying fund status</li>
                            <li>Loss of tax concessions (15% → 45% tax rate)</li>
                            <li>Failed audit</li>
                        </ul>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">✅ 6 Mandatory Elements (Must All Be Addressed)</h4>
                        <div className="space-y-3 text-sm">
                            <div className="bg-green-100 p-3 rounded">
                                <strong className="text-green-900">1. Risk and Return Objectives</strong>
                                <p className="text-xs text-green-700 mt-1">Must be specific and measurable (e.g., "CPI + 3% over 10 years")</p>
                            </div>
                            <div className="bg-green-100 p-3 rounded">
                                <strong className="text-green-900">2. Diversification</strong>
                                <p className="text-xs text-green-700 mt-1">How assets spread across classes, or justification if concentrated</p>
                            </div>
                            <div className="bg-green-100 p-3 rounded">
                                <strong className="text-green-900">3. Liquidity</strong>
                                <p className="text-xs text-green-700 mt-1">Ability to meet pension payments and expenses</p>
                            </div>
                            <div className="bg-green-100 p-3 rounded">
                                <strong className="text-green-900">4. Ability to Pay Benefits</strong>
                                <p className="text-xs text-green-700 mt-1">Can fund pay lump sums when members need them?</p>
                            </div>
                            <div className="bg-green-100 p-3 rounded">
                                <strong className="text-green-900">5. Insurance Needs</strong>
                                <p className="text-xs text-green-700 mt-1">Assessment of life/TPD insurance (even if declined)</p>
                            </div>
                            <div className="bg-green-100 p-3 rounded">
                                <strong className="text-green-900">6. Member Circumstances</strong>
                                <p className="text-xs text-green-700 mt-1">Ages, employment, retirement plans, risk tolerance</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">⚠️ ATO Red Flags (What Gets You in Trouble)</h4>
                        <ul className="list-disc list-inside text-yellow-700 text-sm space-y-1 ml-4">
                            <li><strong>Generic templates</strong> - Using "Member 1" instead of actual names</li>
                            <li><strong>Strategy doesn't match actual investments</strong> - Says "diversified" but 100% in one property</li>
                            <li><strong>Not reviewed annually</strong> - Strategy dated 2020 and never updated</li>
                            <li><strong>Missing elements</strong> - Doesn't address all 6 mandatory items</li>
                            <li><strong>No specific amounts</strong> - Says "adequate liquidity" without calculating actual needs</li>
                        </ul>
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">📝 Sample Compliant Statement</h4>
                        <div className="bg-white p-3 rounded border border-blue-200 text-xs text-gray-700 space-y-2">
                            <p><strong>Member Circumstances:</strong></p>
                            <p className="ml-3">
                                • John Smith (age 62, semi-retired): $800k balance, low-moderate risk tolerance<br/>
                                • Jane Smith (age 58, full-time): $400k balance, moderate risk tolerance
                            </p>
                            
                            <p><strong>Investment Objectives:</strong></p>
                            <p className="ml-3">
                                Target CPI + 3% per annum over rolling 10-year periods (approx 6% p.a. nominal)
                            </p>
                            
                            <p><strong>Asset Allocation:</strong></p>
                            <p className="ml-3">
                                Australian shares 30-40%, International shares 20-30%, Property 20-30%, Bonds 10-20%, Cash 5-15%
                            </p>
                            
                            <p><strong>Liquidity:</strong></p>
                            <p className="ml-3">
                                Maintain $30k cash minimum (6 months expenses). Annual needs: $50k pension + $7k fees = $57k. 
                                Sources: $15k property rental + $24k dividends + $18k share sales = $57k ✓
                            </p>
                            
                            <p><strong>Insurance:</strong></p>
                            <p className="ml-3">
                                John: Not required (no dependents). Jane: $500k life/TPD ($4k/year premium, 2 children)
                            </p>
                            
                            <p><strong>Review:</strong></p>
                            <p className="ml-3">
                                Adopted: Nov 8, 2025. Next review: Nov 8, 2026 or when member circumstances change.
                            </p>
                        </div>
                    </div>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-purple-800 mb-2">🛠️ Investment Strategy Builder (AVAILABLE NOW!)</h4>
                        <p className="text-sm text-purple-700 mb-2">
                            The ATO-compliant Investment Strategy Builder is available in the <strong>"Strategy (ATO Compliant)"</strong> tab:
                        </p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li>✅ Guides you through all 6 mandatory ATO elements</li>
                            <li>✅ Auto-populates from your SMSF calculator data</li>
                            <li>✅ Checks compliance automatically as you complete each section</li>
                            <li>✅ Generates professional audit-ready PDF document</li>
                            <li>✅ Includes trustee minutes template (separate PDF)</li>
                            <li>✅ Annual review checklist PDF for easy maintenance</li>
                        </ul>
                        <div className="mt-3 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Location:</strong> Investment & Assets Category → Strategy (ATO Compliant) tab → Complete the wizard → Download PDFs
                        </div>
                        <div className="mt-2 p-2 bg-green-100 text-xs text-green-900 rounded">
                            <strong>Tip:</strong> Complete your basic SMSF setup first, then use the Strategy Builder. Your member details and asset allocation will auto-populate!
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'analysis',
            title: 'Advanced Analysis & Planning',
            category: 'analysis',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Category 6: Analysis (6 tabs)</h4>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">📈 25. Analytics Tab</h5>
                        <p className="text-sm text-purple-700 mb-2">Performance tracking and attribution:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Returns analysis:</strong> By asset class, time period, member</li>
                            <li><strong>Benchmark comparison:</strong> vs ASX 200, balanced funds, inflation</li>
                            <li><strong>Fee impact:</strong> How much fees reduced returns</li>
                            <li><strong>Contribution tracking:</strong> Total contributed vs investment gains</li>
                        </ul>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">⚠️ 26. Risk Assessment Tab</h5>
                        <p className="text-sm text-red-700 mb-2">Identify and quantify SMSF risks:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Concentration risk:</strong> Too much in one asset (e.g., 80% in one property)</li>
                            <li><strong>Liquidity risk:</strong> Illiquid assets preventing pension payments</li>
                            <li><strong>Market risk:</strong> Portfolio volatility and potential drawdowns</li>
                            <li><strong>Compliance risk:</strong> Potential ATO penalties from breaches</li>
                            <li><strong>Longevity risk:</strong> Running out of money in retirement</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">⚡ 27. Performance Tracking Tab</h5>
                        <p className="text-sm text-green-700 mb-2">Detailed performance metrics:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Unit pricing:</strong> Calculate fair unit prices for members</li>
                            <li><strong>Time-weighted returns:</strong> Adjust for contributions/withdrawals</li>
                            <li><strong>Rolling returns:</strong> 1-year, 3-year, 5-year, 10-year performance</li>
                            <li><strong>Peer comparison:</strong> How does your SMSF compare to average funds?</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🏥 28. Insurance Analysis Tab</h5>
                        <p className="text-sm text-blue-700 mb-2">Life, TPD, and income protection inside SMSF:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Coverage calculator:</strong> How much life/TPD insurance needed</li>
                            <li><strong>Premium affordability:</strong> Can fund afford ongoing premiums</li>
                            <li><strong>Self-insurance:</strong> When super balance is large enough to self-insure</li>
                            <li><strong>Cost comparison:</strong> SMSF insurance vs retail vs group</li>
                        </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">📜 29. Estate Planning Tab</h5>
                        <p className="text-sm text-purple-700 mb-2">Death benefit planning:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Binding nominations:</strong> Valid 3 years, must be renewed</li>
                            <li><strong>Tax on death benefits:</strong> Dependant (tax-free) vs non-dependant (taxed)</li>
                            <li><strong>Reversionary pensions:</strong> Auto-continue to spouse</li>
                            <li><strong>Lump sum vs pension:</strong> Strategic benefit payment planning</li>
                        </ul>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">👨‍👩‍👧‍👦 30. Succession Planning Tab</h5>
                        <p className="text-sm text-orange-700 mb-2">Trustee succession and fund continuity:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Trustee succession:</strong> Who takes over as trustee</li>
                            <li><strong>Enduring power of attorney:</strong> If member loses capacity</li>
                            <li><strong>Wind-up planning:</strong> When/how to close SMSF</li>
                            <li><strong>Member exit strategy:</strong> Rollovers to retail funds</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'reporting',
            title: 'Reporting & Data Management',
            category: 'reporting',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Category 7: Reporting (4 tabs)</h4>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📄 31. Reports Tab</h5>
                        <p className="text-sm text-blue-700 mb-2">Generate PDF reports:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Investment strategy document:</strong> ATO-compliant, trustee-signed</li>
                            <li><strong>Member statements:</strong> Annual balance and transaction summary</li>
                            <li><strong>Contribution summary:</strong> Caps used, available, warnings</li>
                            <li><strong>Compliance checklist:</strong> Pre-audit review document</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📧 32. Email Reports Tab</h5>
                        <p className="text-sm text-green-700 mb-2">Automated email reporting:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Compliance alerts:</strong> Email when approaching contribution caps</li>
                            <li><strong>Quarterly summaries:</strong> Performance and compliance updates</li>
                            <li><strong>Deadline reminders:</strong> Tax return, audit, annual return due dates</li>
                        </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">📈 33. History & Trends Tab</h5>
                        <p className="text-sm text-purple-700 mb-2">Long-term fund analysis:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Balance growth over time:</strong> Chart showing fund growth since establishment</li>
                            <li><strong>Contribution history:</strong> Annual contributions by type</li>
                            <li><strong>Fee trends:</strong> How fees changed over time</li>
                            <li><strong>Return patterns:</strong> Best/worst years identified</li>
                        </ul>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">💾 34. Data Management Tab</h5>
                        <p className="text-sm text-orange-700 mb-2">Import/export and backup:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Export to Excel:</strong> All data for external analysis</li>
                            <li><strong>Export to CSV:</strong> Import into accounting software</li>
                            <li><strong>Save scenarios:</strong> Store different planning scenarios</li>
                            <li><strong>Load saved data:</strong> Restore previous calculations</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'strategy',
            title: 'Investment Strategy & Enhanced Features',
            category: 'strategy',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Category 8: Strategy Tools (2 tabs)</h4>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">🎯 35. Strategy Builder Tab</h5>
                        <p className="text-sm text-green-700 mb-2">Create comprehensive investment strategy:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Investment objectives:</strong> Text field (min 50 chars for PDF generation)</li>
                            <li><strong>Risk tolerance:</strong> Low, Medium, High, Custom</li>
                            <li><strong>Asset allocation:</strong> Detailed breakdown with constraints</li>
                            <li><strong>Rebalancing strategy:</strong> When and how to rebalance</li>
                            <li><strong>Liquidity requirements:</strong> Cash needed for expenses/pensions</li>
                            <li><strong>Review frequency:</strong> Quarterly, Semi-annual, Annual, Biennial</li>
                        </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>ATO requirement:</strong> Must have written investment strategy, reviewed and signed annually by trustees
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🚀 36. Enhanced Features Tab</h5>
                        <p className="text-sm text-purple-700 mb-2">Document generation and compliance tools:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Document Generation:</strong> Create Trust Deed, Trustee Minutes, Investment Resolution, Compliance Checklist</li>
                            <li><strong>ATO Reports:</strong> SMSF Annual Return, Member Contributions Statement, Compliance Report</li>
                            <li><strong>Compliance Calendar:</strong> Track deadlines, tasks, and compliance score with interactive calendar</li>
                        </ul>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Note:</strong> All documents are templates that can be customized. Annual Return data should be verified with your accountant.
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🎓 Using All Tabs Effectively</h5>
                        <p className="text-sm text-blue-700 mb-2"><strong>Recommended workflow:</strong></p>
                        <div className="bg-blue-100 p-3 rounded text-xs space-y-1 mt-2">
                            <div><strong>1. Setup (Tabs 1-4):</strong> Configure fund details, run wizard</div>
                            <div><strong>2. Contributions (Tabs 5-12):</strong> Plan annual contributions using relevant calculators</div>
                            <div><strong>3. Investment (Tabs 13-18):</strong> Build strategy, check limits, manage assets</div>
                            <div><strong>4. Pension (Tabs 19-21):</strong> Optimize pension phase, model cash flow</div>
                            <div><strong>5. Compliance (Tabs 22-24):</strong> Check compliance status, prepare for audit</div>
                            <div><strong>6. Analysis (Tabs 25-30):</strong> Deep dive into performance, risk, estate planning</div>
                            <div><strong>7. Reporting (Tabs 31-34):</strong> Generate required documents and reports</div>
                            <div><strong>8. Review & Update (Tabs 35-36):</strong> Annual strategy review using enhanced tools</div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'practical',
            title: 'Practical SMSF Management Tips',
            category: 'tips',
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Best Practices for SMSF Success:</h4>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">✅ Annual SMSF Calendar</h5>
                        <div className="text-sm text-green-700 space-y-2">
                            <div><strong>July 1 - End of Financial Year:</strong></div>
                            <ul className="list-disc list-inside text-green-600 text-xs ml-4">
                                <li>Finalize year-end position</li>
                                <li>Collect tax invoices and receipts</li>
                                <li>Prepare for audit</li>
                            </ul>
                            <div><strong>July-September:</strong></div>
                            <ul className="list-disc list-inside text-green-600 text-xs ml-4">
                                <li>Complete tax return</li>
                                <li>SMSF audit completed</li>
                                <li>Lodge annual return with ATO (due October 31)</li>
                            </ul>
                            <div><strong>Quarterly:</strong></div>
                            <ul className="list-disc list-inside text-green-600 text-xs ml-4">
                                <li>Review investment performance</li>
                                <li>Pay member pensions</li>
                                <li>Check contribution caps</li>
                            </ul>
                            <div><strong>Annually:</strong></div>
                            <ul className="list-disc list-inside text-green-600 text-xs ml-4">
                                <li>Trustee meeting (with minutes)</li>
                                <li>Investment strategy review</li>
                                <li>Member statements issued</li>
                                <li>Update binding death nominations (3-year expiry)</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">❌ Top 10 SMSF Compliance Breaches</h5>
                        <ul className="list-disc list-inside text-red-700 text-sm space-y-1 ml-4">
                            <li><strong>1. In-house assets &gt; 5%:</strong> Most common breach (e.g., loan to related business)</li>
                            <li><strong>2. Failing sole purpose test:</strong> Using SMSF assets personally</li>
                            <li><strong>3. Exceeding contribution caps:</strong> Not tracking concessional/non-concessional totals</li>
                            <li><strong>4. Related party at non-commercial terms:</strong> Below-market rent to family business</li>
                            <li><strong>5. Residential property to member/relative:</strong> Cannot lease home to family</li>
                            <li><strong>6. Late lodgement:</strong> Missing ATO annual return deadline</li>
                            <li><strong>7. Not meeting minimum pension:</strong> Age-based minimums must be paid</li>
                            <li><strong>8. Collectible personal use:</strong> Wearing jewelry, driving classic car from fund</li>
                            <li><strong>9. Inadequate insurance:</strong> Trustee requirements not met</li>
                            <li><strong>10. No investment strategy:</strong> Every SMSF must have one</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">💰 Cost Management Tips</h5>
                        <p className="text-sm text-blue-700 mb-2">Minimize SMSF running costs:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Accounting:</strong> $1,000-$2,000 (shop around, some charge by complexity)</li>
                            <li><strong>Audit:</strong> $400-$800 (legally required, independent auditor)</li>
                            <li><strong>Admin/Software:</strong> $200-$600 (Class Super, BGL Simple Fund, etc.)</li>
                            <li><strong>Legal advice:</strong> Budget $500-$2,000 for deed updates, complex queries</li>
                            <li><strong>Total typical:</strong> $1,500-$3,500/year minimum</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Break-even:</strong> On $200k fund, 1.5% in fees = $3k/year. Need &gt; $300-400k for SMSF to be cost-competitive
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">🎯 When to Consider Winding Up SMSF</h5>
                        <ul className="list-disc list-inside text-yellow-700 text-sm space-y-1 ml-4">
                            <li><strong>Balance fallen below $200k:</strong> Fees become disproportionate</li>
                            <li><strong>Trustee incapacity:</strong> Member can't fulfill trustee duties</li>
                            <li><strong>Member conflict:</strong> Disagreements between family members</li>
                            <li><strong>Time burden:</strong> Compliance requirements too onerous</li>
                            <li><strong>Lifestyle changes:</strong> Traveling, moving overseas</li>
                        </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">📚 Essential Resources</h5>
                        <div className="text-sm text-purple-700 space-y-1">
                            <div><strong>ATO:</strong> ato.gov.au/super/smsf (official rules and forms)</div>
                            <div><strong>ASIC:</strong> Trustee responsibilities and education</div>
                            <div><strong>ATO SMSF Regulator:</strong> Compliance bulletins</div>
                            <div><strong>Professional advice:</strong> SMSF specialist accountant and financial advisor</div>
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">✅ SMSF Advantages</h5>
                        <ul className="list-disc list-inside text-green-700 text-sm space-y-1 ml-4">
                            <li><strong>Control:</strong> Direct investment decisions</li>
                            <li><strong>Flexibility:</strong> Unique strategies (e.g., business property)</li>
                            <li><strong>Tax planning:</strong> Sophisticated tax minimization</li>
                            <li><strong>Estate planning:</strong> Greater control over death benefits</li>
                            <li><strong>Cost effective:</strong> If balance &gt; $300-500k</li>
                        </ul>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">⚠️ SMSF Disadvantages</h5>
                        <ul className="list-disc list-inside text-red-700 text-sm space-y-1 ml-4">
                            <li><strong>Time commitment:</strong> 50-100+ hours/year</li>
                            <li><strong>Complexity:</strong> Must understand super and tax law</li>
                            <li><strong>Responsibility:</strong> Trustees personally liable for breaches</li>
                            <li><strong>Costs:</strong> High fixed costs (not suitable for small balances)</li>
                            <li><strong>No compensation:</strong> No government compensation scheme like APRA funds</li>
                            <li><strong>Investment risk:</strong> No professional fund manager</li>
                        </ul>
                    </div>
                </div>
            )
        }
    ];

    const progressPercent = ((currentStep + 1) / tutorialSteps.length) * 100;

    const innerContent = (
        <div className="flex flex-col flex-1 min-h-0">
                {/* Progress Bar */}
                <div className="px-6 py-3 bg-gray-50 border-b">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">
                            Step {currentStep + 1} of {tutorialSteps.length}
                        </span>
                        <span className="text-xs text-gray-500">
                            {tutorialSteps[currentStep].category.toUpperCase()}
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                            className="bg-gradient-to-r from-blue-600 to-purple-600 h-3 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                            style={{ width: `${progressPercent}%` }}
                        >
                            <span className="text-[10px] text-white font-bold">{Math.round(progressPercent)}%</span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div ref={contentRef} className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-4xl mx-auto">
                        <h3 className="text-3xl font-bold text-gray-900 mb-6">
                            {tutorialSteps[currentStep].title}
                        </h3>
                        <div className="prose prose-blue max-w-none">
                            {tutorialSteps[currentStep].content}
                        </div>
                    </div>
                </div>

                {/* Navigation Footer */}
                <div className="border-t bg-gray-50 p-4">
                    <div className="flex items-center gap-3 max-w-4xl mx-auto">
                        <button
                            onClick={() => {
                                const newStep = Math.max(0, currentStep - 1);
                                setCurrentStep(newStep);
                                // Force scroll on click
                                setTimeout(() => {
                                    if (contentRef.current) {
                                        contentRef.current.scrollTop = 0;
                                    }
                                }, 0);
                            }}
                            disabled={currentStep === 0}
                            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold shrink-0 whitespace-nowrap"
                        >
                            ← Previous
                        </button>
                        
                        {/* Step Dots */}
                        <div className="flex-1 min-w-0 flex gap-2 items-center justify-center overflow-x-auto px-1">
                            {tutorialSteps.map((step, idx) => (
                                <button
                                    key={step.id}
                                    onClick={() => {
                                        setCurrentStep(idx);
                                        // Force scroll when clicking dots
                                        setTimeout(() => {
                                            if (contentRef.current) {
                                                contentRef.current.scrollTop = 0;
                                            }
                                        }, 0);
                                    }}
                                    className={`rounded-full transition-all ${
                                        idx === currentStep 
                                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 w-12 h-3' 
                                            : 'bg-gray-300 hover:bg-gray-400 w-3 h-3'
                                    }`}
                                    title={step.title}
                                />
                            ))}
                        </div>
                        
                        {currentStep === tutorialSteps.length - 1 ? (
                            <button
                                onClick={onClose}
                                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold shrink-0 whitespace-nowrap"
                            >
                                ✓ Finish Tutorial
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    const newStep = Math.min(tutorialSteps.length - 1, currentStep + 1);
                                    setCurrentStep(newStep);
                                    // Force scroll on Next click
                                    setTimeout(() => {
                                        if (contentRef.current) {
                                            contentRef.current.scrollTop = 0;
                                        }
                                    }, 0);
                                }}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold shrink-0 whitespace-nowrap"
                            >
                                Next →
                            </button>
                        )}
                    </div>
                </div>

                {/* Quick Navigation Panel */}
                <div className="border-t bg-gradient-to-r from-gray-50 to-blue-50 p-3">
                    <div className="max-w-4xl mx-auto">
                        <button
                            onClick={() => setExpandedCategory(expandedCategory ? null : 'all')}
                            className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                        >
                            {expandedCategory ? '▼ Hide' : '▶ Show'} All 37 Tabs Quick Reference
                        </button>
                        {expandedCategory && (
                            <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                                {/* Overview */}
                                <div className="bg-white p-2 rounded shadow-sm">
                                    <div className="font-semibold text-blue-900 mb-1">Overview (4)</div>
                                    <div className="text-gray-600 space-y-0.5">
                                        <div>👤 Fund Details</div>
                                        <div>📊 Dashboard</div>
                                        <div>🧙‍♂️ Wizard</div>
                                        <div>⚖️ Comparison</div>
                                    </div>
                                </div>
                                {/* Contributions */}
                                <div className="bg-white p-2 rounded shadow-sm">
                                    <div className="font-semibold text-green-900 mb-1">Contributions (9)</div>
                                    <div className="text-gray-600 space-y-0.5">
                                        <div>📅 Carry-Forward</div>
                                        <div>🔄 Bring-Forward</div>
                                        <div>💚 Co-Contribution</div>
                                        <div>🏠 Downsizer</div>
                                        <div>💵 Division 293</div>
                                        <div>🏦 Division 296</div>
                                        <div>⏱️ Work Test</div>
                                        <div>🏡 FHSS</div>
                                        <div>💑 Spouse Split</div>
                                    </div>
                                </div>
                                {/* Investment */}
                                <div className="bg-white p-2 rounded shadow-sm">
                                    <div className="font-semibold text-purple-900 mb-1">Investment (7)</div>
                                    <div className="text-gray-600 space-y-0.5">
                                        <div>📊 Strategy</div>
                                        <div>⚖️ Strategy (ATO)</div>
                                        <div>🏛️ LRBA</div>
                                        <div>📊 Limits Monitor</div>
                                        <div>🎨 Collectibles</div>
                                        <div>👥 Related Party</div>
                                        <div>🏠 Valuation</div>
                                    </div>
                                </div>
                                {/* Pension & Analysis */}
                                <div className="bg-white p-2 rounded shadow-sm">
                                    <div className="font-semibold text-orange-900 mb-1">Pension (2)</div>
                                    <div className="text-gray-600 space-y-0.5">
                                        <div>⚡ Quick Check</div>
                                        <div>📉 Tax Harvest</div>
                                    </div>
                                    <div className="font-semibold text-red-900 mt-2 mb-1">Compliance (3)</div>
                                    <div className="text-gray-600 space-y-0.5">
                                        <div>✅ Compliance</div>
                                        <div>🛡️ Enhanced</div>
                                        <div>📋 Admin</div>
                                    </div>
                                </div>
                                {/* Analysis */}
                                <div className="bg-white p-2 rounded shadow-sm">
                                    <div className="font-semibold text-yellow-900 mb-1">Analysis (6)</div>
                                    <div className="text-gray-600 space-y-0.5">
                                        <div>📈 Analytics</div>
                                        <div>⚠️ Risk</div>
                                        <div>⚡ Performance</div>
                                        <div>🏥 Insurance</div>
                                        <div>📜 Estate</div>
                                        <div>👨‍👩‍👧‍👦 Succession</div>
                                    </div>
                                </div>
                                {/* Reporting */}
                                <div className="bg-white p-2 rounded shadow-sm">
                                    <div className="font-semibold text-indigo-900 mb-1">Reporting (4)</div>
                                    <div className="text-gray-600 space-y-0.5">
                                        <div>📄 Reports</div>
                                        <div>📧 Email</div>
                                        <div>📈 History</div>
                                        <div>💾 Data</div>
                                    </div>
                                </div>
                                {/* Strategy */}
                                <div className="bg-white p-2 rounded shadow-sm">
                                    <div className="font-semibold text-pink-900 mb-1">Strategy (2)</div>
                                    <div className="text-gray-600 space-y-0.5">
                                        <div>🎯 Strategy Builder</div>
                                        <div>🚀 Enhanced</div>
                                    </div>
                                </div>
                                <div className="bg-blue-100 p-2 rounded shadow-sm col-span-1">
                                    <div className="font-semibold text-blue-900 text-center">36 Total Tools</div>
                                    <div className="text-xs text-blue-700 text-center mt-1">Click tabs above to explore</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
        </div>
    );

    if (renderInPanel) {
        return <div className="flex-1 flex flex-col overflow-hidden min-h-0">{innerContent}</div>;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">SMSF Calculator Tutorial</h2>
                        <p className="text-sm text-gray-600 mt-1">Comprehensive guide to all 36 features</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
                        aria-label="Close tutorial"
                    >
                        ×
                    </button>
                </div>
                {innerContent}
            </div>
        </div>
    );
};

export default SMSFTutorialSystem;
